import { useCallback } from "react"
import { useAppSelector, useAppDispatch } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import { getFormByName } from "@/core/app/slices/uassist/form.util"
import FORM_CONSTANT from "@/core/app/slices/uassist/form.constant"
import { caseManagement, saveArchType } from "@/gluelayer"
import { uploadCaseZips } from "@/core/app/slices/clinical/clinicalThunkApi"
import { updateFormSavedData } from "@/core/app/slices/uassist/formThunkApi"
import { setIsRouteChangeBlocked } from "@/core/app/slices/navigationPopup/navigationPopupSlice"
import { useParams, matchPath, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { CASE_STATUS } from "@/modules/Patient/config/status.config"
import {
  getCaseById,
  updateCaseById,
  updateCaseStatus,
} from "@/core/app/slices/case/caseThunkApi"
import { FormType } from "../types"
import { updateRetainerFasttrackOrderType } from "@/core/app/slices/order/orderSlice"
import { RetainerFasttrackOrderType } from "@/core/model/interface/AlignerOrder"
import { isRetainerFastrack, toError } from "../formUtils"

/**
 * Type for an arch to treat in the form.
 */
interface ArchToTreat {
  aligner_type: "Aligner" | "Retainer"
}

/**
 * Type for arches to treat in the form.
 */
interface ArchesToTreat {
  upper?: ArchToTreat
  lower?: ArchToTreat
}

/**
 * Type for form data containing arches to treat.
 */
interface FormData {
  arches_to_treat: ArchesToTreat
}

/**
 * Parameters for fileUploadAndFormSave in useFormSave.
 */
interface FileUploadAndFormSaveParams {
  formType: FormType
  formName: string
  data: Record<string, any>
  saveId: string
  isrefinement?: boolean
  popAlert: (message: string, isError?: boolean) => void
}

/**
 * Parameters for handleAttentionNeededFormSave in useFormSave.
 */
interface HandleAttentionNeededFormSaveParams {
  formType: FormType
  formName: string
  data: Record<string, any>
  caseDetail: any
  clinicalSettings: any
  saveId: string
  popAlert: (message: string, isError?: boolean) => void
  setShowUNoUassistAlert: (val: boolean) => void
}

/**
 * Helper to get arch type code ("A" or "R") from arch type.
 */
const getArchTypeCode = (archType?: ArchToTreat): string => {
  if (!archType) return "--"
  return archType.aligner_type === "Aligner" ? "A" : "R"
}

/**
 * Helper to get arch type number (0 or 1) from arch type.
 */
const getArchTypeNumber = (archType?: ArchToTreat): number | null => {
  if (!archType) return null
  return archType.aligner_type === "Aligner" ? 0 : 1
}

const isClinicalPatientPath = (pathname: string): boolean => {
  return !!matchPath({
    path: "/clinical/patient/:patientId/case/:caseId/*",
    end: false
  }, pathname);
}

const isRefinementForm = (formName: string): boolean => {
  const formNameLower = formName.toLowerCase();
  return [
    FORM_CONSTANT.REFINEMENT.toLowerCase(),
    FORM_CONSTANT.REFINEMENT_2.toLowerCase()
  ].includes(formNameLower);
}

const shouldIncludeRefinementFlag = (formName: string, pathname: string): boolean => {
  return isRefinementForm(formName) && isClinicalPatientPath(pathname);
}

const shouldUpdateStatus = (formName: string, pathname: string): boolean => {
  const formNameLower = formName.toLowerCase();
  const isInClinicalPath = isClinicalPatientPath(pathname);
  
  if ([FORM_CONSTANT.RETAINER.toLowerCase(),
     FORM_CONSTANT.PRESCRIPTION.toLowerCase()].includes(formNameLower)) {
    return !isInClinicalPath;
  }
  else if (isRefinementForm(formName)) {
    return isInClinicalPath;
  }

  return false;
}

/**
 * Custom hook for saving forms, uploading files, and updating case data.
 * Provides several helpers for different save flows.
 */
export const useFormSave = () => {
  const { formTemplateList } = useAppSelector(
    (state: RootState) => state.formService,
  )
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { caseId, patientId } = useParams<{
    caseId: string
    patientId: string
  }>()
  const { t } = useTranslation("common")
  const { clinicalSettings, clinicalSettingsFetchStatus } = useAppSelector(
    (state: RootState) => state.userService,
  )
  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)

  /**
   * Saves clinical settings to zip, updates case details, and uploads case files.
   */
  const postSettingTozips = useCallback(
    async (data: FormData, isRefinement: boolean=null): Promise<void> => {
      if (!clinicalSettingsFetchStatus) return
      try {
        await caseManagement.saveJsonDataToZip(
          JSON.stringify(clinicalSettings),
          "uassist_team_settings.json",
          "Setting Data2",
        )
        if (caseDetail && !isRefinement) {
          /*** 
           * When saving/submiting the form for Refinement cases, no need to update the
           * caseDetail->udesign_json or Bulk2->ArchType.json file. weD will update these values
           */
          const udesign_json = JSON.parse(caseDetail.udesign_json)

          let upperTypeVal, lowerTypeVal
          if (Array.isArray(data.arches_to_treat)) {
            upperTypeVal = data.arches_to_treat.includes("upper")
              ? udesign_json.UpperType
              : "--"
            lowerTypeVal = data.arches_to_treat.includes("lower")
              ? udesign_json.LowerType
              : "--"
          } else {
            upperTypeVal = getArchTypeCode(data.arches_to_treat.upper)
            lowerTypeVal = getArchTypeCode(data.arches_to_treat.lower)
          }
          const newUdesignJsonObj = {
            ...udesign_json,
            UpperType: upperTypeVal,
            LowerType: lowerTypeVal,
          }
          await dispatch(
            updateCaseById({
              patientId,
              caseId,
              payload: {
                case_extra_attrs: JSON.stringify(newUdesignJsonObj),
              },
            }),
          ).unwrap()
          saveArchType(
            getArchTypeNumber(data.arches_to_treat.upper),
            getArchTypeNumber(data.arches_to_treat.lower),
          )
        }
        const caseFiles = await caseManagement.getCaseFiles(["bulk2", "bulk10"])
        if (caseFiles) {
          const zips = Object.entries(caseFiles).map(([fileName, file]) => ({
            fileName,
            file,
          }))
          await dispatch(
            uploadCaseZips({
              zips,
              patientId,
              caseId,
              skipLockCheck: null,
            }),
          ).unwrap()
        }
      } catch (error) {
        throw toError(
          error,
          "Failed to save clinical settings and upload case zips",
        )
      }
    },
    [
      clinicalSettings,
      clinicalSettingsFetchStatus,
      caseDetail,
      dispatch,
      patientId,
      caseId,
    ],
  )

  /**
   * Uploads files and saves form data, handling both refinement and non-refinement flows.
   */
  const fileUploadAndFormSave = useCallback(
    async ({
      formType,
      formName,
      data,
      saveId,
      isrefinement = false,
      popAlert,
    }: FileUploadAndFormSaveParams): Promise<void> => {
      try {
        const r = await caseManagement.getCaseFiles(["bulk2", "bulk10"])
        const zips: { fileName: string; file: any }[] = []
        if (r) {
          for (const k in r) {
            zips.push({
              fileName: k,
              file: r[k],
            })
          }
          if (formType !== FormType.SMARTRX) {
            await dispatch(
              uploadCaseZips({
                zips,
                patientId,
                caseId,
                skipLockCheck: null,
              }),
            )
          }
          try {
            const res: any = await dispatch(
              updateFormSavedData({
                formId: getFormByName(formTemplateList, formType, formName).id,
                caseId: caseId,
                payload: data,
                savedId: saveId,
                isRefinement: isrefinement,
              }),
            )
            if (
              res.payload.status === 200 &&
              getFormByName(formTemplateList, formType, formName).id
            ) {
              dispatch(
                setIsRouteChangeBlocked({
                  isRouteChangeBlocked: false,
                }),
              )
              popAlert(t("uassist.uAssistForm.prescriptionSaveStatusSuccess"))
            } else {
              popAlert(
                t("uassist.uAssistForm.prescriptionSaveStatusError"),
                true,
              )
            }
          } catch (formSaveError) {
            throw toError(
              formSaveError,
              "Failed to save form data after file upload",
            )
          }
        } else {
          throw new Error("File upload and form save error")
        }
      } catch (error) {
        throw toError(error, "Failed to upload files and save form data")
      }
    },
    [formTemplateList, dispatch, caseId, patientId, t],
  )

  /**
   * Handles refinement processing for both refinement1 and refinement2 forms.
   */
  const handleRefinementProcessing = useCallback(
    async ({
      formId,
      data,
      caseDetail,
      clinicalSettings,
    }: {
      formId: string
      data: any
      caseDetail: any
      clinicalSettings: any
    }): Promise<void> => {
      let isUpperArchPresent = false
      let isLowerArchPresent = false

      if (
        formId ===
        getFormByName(
          formTemplateList,
          FormType.UASSIST,
          FORM_CONSTANT.REFINEMENT_2,
        ).id
      ) {
        isUpperArchPresent = data?.arches_to_treat?.upper
        isLowerArchPresent = data?.arches_to_treat?.lower
      } else {
        isUpperArchPresent = data?.arches_to_treat?.includes("upper")
        isLowerArchPresent = data?.arches_to_treat?.includes("lower")
      }

      try {
        const isArchSaved = await caseManagement.saveDoRefinementStage(
          !isUpperArchPresent
            ? "-1010"
            : data?.current_aligner_number?.Upper,
          !isLowerArchPresent
            ? "-1010"
            : data?.current_aligner_number?.Lower,
        )

        const CaseType = caseDetail.udesign_category.includes("A")
          ? "Aligner"
          : "Retainer"

        const getUpperArch = () => {
          if (
            formId ===
            getFormByName(
              formTemplateList,
              FormType.UASSIST,
              FORM_CONSTANT.REFINEMENT_2,
            ).id
          ) {
            return data?.arches_to_treat?.upper?.aligner_type
          } else {
            return CaseType
          }
        }

        const getLowerArch = () => {
          if (
            formId ===
            getFormByName(
              formTemplateList,
              FormType.UASSIST,
              FORM_CONSTANT.REFINEMENT_2,
            ).id
          ) {
            return data?.arches_to_treat?.lower?.aligner_type
          } else {
            return CaseType
          }
        }

        try {
          const isJsonUpdatedToZip =
            await caseManagement.saveJsonDataToZip(
              JSON.stringify({
                upperTPStep: !isUpperArchPresent
                  ? "-1010"
                  : data?.current_aligner_number?.Upper ?? "0",
                lowerTPStep: !isLowerArchPresent
                  ? "-1010"
                  : data?.current_aligner_number?.Lower ?? "0",
                UpperArch: !isUpperArchPresent ? "" : getUpperArch(),
                LowerArch: !isLowerArchPresent ? "" : getLowerArch(),
              }),
              "paras.json",
              "Setting Data10/refinement",
            )
          if (isJsonUpdatedToZip && isArchSaved) {
            try {
              const res = await caseManagement.saveJsonDataToZip(
                JSON.stringify(clinicalSettings),
                "uassist_team_settings.json",
                "Setting Data2",
              )
            } catch (clinicalSettingsError) {
              throw toError(
                clinicalSettingsError,
                "Failed to save clinical settings for refinement",
              )
            }
          } else {
            throw new Error("Failed to save arch data or JSON data")
          }
        } catch (jsonSaveError) {
          throw toError(
            jsonSaveError,
            "Failed to save refinement JSON data to zip",
          )
        }
      } catch (archSaveError) {
        throw toError(
          archSaveError,
          "Failed to save refinement arch data to zip",
        )
      }
    },
    [formTemplateList, fileUploadAndFormSave],
  )

  /**
   * Handles saving for "Attention Needed" forms, including NO_UASSIST logic.
   */
  const handleAttentionNeededFormSave = useCallback(
    async ({
      formType,
      formName,
      data,
      caseDetail,
      clinicalSettings,
      saveId,
      popAlert,
      setShowUNoUassistAlert,
    }: HandleAttentionNeededFormSaveParams): Promise<void> => {
      try {
        if (caseDetail && caseDetail.uassist_progress === "NO_UASSIST") {
          setShowUNoUassistAlert(true)
        } else {
          const formId = getFormByName(formTemplateList, formType, formName).id
          const refinement1 =
            formId ===
            getFormByName(formTemplateList, FormType.UASSIST, FORM_CONSTANT.REFINEMENT)
              .id
          const refinement2 =
            formId ===
            getFormByName(
              formTemplateList,
              FormType.UASSIST,
              FORM_CONSTANT.REFINEMENT_2,
            ).id

          if (refinement1 || refinement2) {
            await handleRefinementProcessing({
              formId,
              data,
              caseDetail,
              clinicalSettings,
            })
          }
            await fileUploadAndFormSave({
              formType,
              formName,
              data,
              saveId,
              isrefinement: refinement1 || refinement2,
              popAlert,
            })
        }
      } catch (error) {
        throw toError(
          error,
          "Failed to save attention-needed form data for Uassist",
        )
      }
    },
    [formTemplateList, caseDetail, patientId, caseId, handleRefinementProcessing],
  )

  const handleNewPatientFormSave = useCallback(
    async ({
      formType,
      formName,
      data,
      saveId,
      details,
      popAlert,
    }: {
      formType: FormType
      formName: string
      data: any
      saveId: string
      details: any
      popAlert: (message: string, isError?: boolean) => void
    }) => {
      const isRefinement = [
        FORM_CONSTANT.REFINEMENT.toLowerCase(),
        FORM_CONSTANT.REFINEMENT_2.toLowerCase(),
      ].includes(formName.toLowerCase())
      try {
        await dispatch(
          getCaseById({
            patientId: details?.patientId,
            caseId: details?.caseId,
          }),
        )
        if (isRefinement) {
          await handleRefinementProcessing({
            formId: getFormByName(formTemplateList, formType, formName).id,
            data,
            caseDetail,
            clinicalSettings,
          })
        }
        await dispatch(
          updateFormSavedData({
            formId: getFormByName(formTemplateList, formType, formName).id,
            caseId: details?.caseId.toString(),
            payload: data,
            savedId: saveId,
            isRefinement: isRefinement,
          }),
        )
        const isRetainer = formName.toLowerCase() === FORM_CONSTANT.RETAINER.toLowerCase()
        
      
        if (formType === FormType.SMARTRX) return
        await postSettingTozips(data, isRefinement)
        // Only update case status if disposition matches
        if((isRetainer || isRefinement) && isRetainerFastrack(isRetainer, isRefinement, data) && !details?.saveAndContinueLaterFromPhotos){
          dispatch(updateRetainerFasttrackOrderType( data?.bracket_removal?.value?.includes("yes") ? RetainerFasttrackOrderType.BRACKET_REMOVAL_UASSIST : RetainerFasttrackOrderType.RETAINER_UASSIST))
          return
        }
        if (shouldUpdateStatus(formName, location.pathname) && !details?.saveAndContinueLaterFromPhotos
        ) {
          if(caseDetail.caseDisposition !== CASE_STATUS?.SUBMIT_TO_UASSIST){
            await dispatch(
              updateCaseStatus({
                patientId: details?.patientId,
                caseId: details?.caseId,
                payload: { 
                  case_disposition: CASE_STATUS?.SUBMIT_TO_UASSIST,
                  ...(shouldIncludeRefinementFlag(formName, location.pathname) && { is_refinement: true })
                },
              }),
            )
            await dispatch(
              getCaseById({
                patientId: details?.patientId,
                caseId: details?.caseId,
              }),
            )
          }
        }
      } catch (error) {
        popAlert &&
          popAlert(t("uassist.uAssistForm.prescriptionSaveStatusError"), true)
        throw toError(
          error,
          "Failed to save prescription data for new patient flow",
        )
      }
    },
    [formTemplateList, dispatch, caseDetail, t, patientId, caseId,],
  )

  return { handleAttentionNeededFormSave, handleNewPatientFormSave }
}
